'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/Button';
import { RichText } from '@/components/ui/RichText';
import styles from './DeviceShowcase.module.css';

interface DeviceShowcaseProps {
  translations: {
    title: string;
    subtitle: string;
    devices: {
      tablet: { label: string; description?: string };
      qr: { label: string; description?: string };
      kiosk: { label: string; description?: string };
      webshop: { label: string; description?: string };
    };
    images?: {
      products?: {
        tablet?: string;
        qr?: string;
        kiosk?: string;
        webshop?: string;
        tabletPokeGreen?: string;
        qrPokeGreen?: string;
        kioskPokeGreen?: string;
        webshopPokeGreen?: string;
        tabletAnneMax?: string;
        qrAnneMax?: string;
        kioskAnneMax?: string;
        webshopAnneMax?: string;
      };
    };
  };
  ctaText?: string;
  ctaUrl?: string;
  ctaId?: string;
  ctaPosition?: string;
  locale?: string; // Add locale for product page links
}

const IMAGE_SETS = [
  {
    tablet: '/images/mockups/tablet-mockup.webp',
    qr: '/images/mockups/qr-mockup.webp',
    kiosk: '/images/mockups/kiosk-mockup.webp',
    webshop: '/images/mockups/webshop-mockup.webp',
  },
  {
    tablet: '/images/mockups/tablet-mockup-poke-green.webp',
    qr: '/images/mockups/qr-mockup-poke-green.webp',
    kiosk: '/images/mockups/kiosk-mockup-poke-green.webp',
    webshop: '/images/mockups/macbook-mockup-poke-green.webp',
  },
  {
    tablet: '/images/mockups/tablet-mockup-anne-max.webp',
    qr: '/images/mockups/qr-mockup-anne-max.webp',
    kiosk: '/images/mockups/kiosk-mockup-anne-max.webp',
    webshop: '/images/mockups/webshop-mockup-anne-max.webp',
  },
  {
    tablet: '/images/mockups/tablet-mockup-poke.webp',
    qr: '/images/mockups/qr-mockup-poke.webp',
    kiosk: '/images/mockups/kiosk-mockup-poke.webp',
    webshop: '/images/mockups/macbook-mockup-poke.webp',
  },
];

// Better image preloading using native browser Image constructor
function preloadImage(src: string) {
  if (typeof window !== 'undefined') {
    const img = document.createElement('img');
    img.src = src;
  }
}

export default function DeviceShowcase({ 
  translations,
  ctaText,
  ctaUrl,
  ctaId,
  ctaPosition,
  locale = 'en'
}: DeviceShowcaseProps) {
  const [currentSet, setCurrentSet] = useState(0);
  
  // Generate product page URLs based on locale
  const getProductUrl = (product: 'tablet' | 'qr' | 'kiosk' | 'webshop') => {
    const urls: Record<string, Record<string, string>> = {
      tablet: {
        en: '/tablet-ordering',
        nl: '/nl/tablet-bestellen',
        de: '/de/tablet-bestellung'
      },
      qr: {
        en: '/qr-ordering',
        nl: '/nl/qr-bestellen',
        de: '/de/qr-bestellung'
      },
      kiosk: {
        en: '/self-ordering-kiosk',
        nl: '/nl/bestelzuil',
        de: '/de/bestellterminal-gastronomie'
      },
      webshop: {
        en: '/online-ordering-webshop',
        nl: '/nl/online-bestellen-webshop',
        de: '/de/online-bestell-webshop'
      }
    };
    return urls[product][locale] || urls[product]['en'];
  };

  // Progressive image preloading - preload next set 2 seconds before it's shown
  useEffect(() => {
    // Preload Set 2 after 1.5 seconds (shows at 3.5s)
    const preloadSet2Timer = setTimeout(() => {
      IMAGE_SETS[1].tablet && preloadImage(IMAGE_SETS[1].tablet);
      IMAGE_SETS[1].qr && preloadImage(IMAGE_SETS[1].qr);
      IMAGE_SETS[1].kiosk && preloadImage(IMAGE_SETS[1].kiosk);
      IMAGE_SETS[1].webshop && preloadImage(IMAGE_SETS[1].webshop);
    }, 1500);

    // Preload Set 3 after 5 seconds (shows at 7s)
    const preloadSet3Timer = setTimeout(() => {
      IMAGE_SETS[2].tablet && preloadImage(IMAGE_SETS[2].tablet);
      IMAGE_SETS[2].qr && preloadImage(IMAGE_SETS[2].qr);
      IMAGE_SETS[2].kiosk && preloadImage(IMAGE_SETS[2].kiosk);
      IMAGE_SETS[2].webshop && preloadImage(IMAGE_SETS[2].webshop);
    }, 5000);

    // Preload Set 4 after 8.5 seconds (shows at 10.5s)
    const preloadSet4Timer = setTimeout(() => {
      IMAGE_SETS[3].tablet && preloadImage(IMAGE_SETS[3].tablet);
      IMAGE_SETS[3].qr && preloadImage(IMAGE_SETS[3].qr);
      IMAGE_SETS[3].kiosk && preloadImage(IMAGE_SETS[3].kiosk);
      IMAGE_SETS[3].webshop && preloadImage(IMAGE_SETS[3].webshop);
    }, 8500);

    return () => {
      clearTimeout(preloadSet2Timer);
      clearTimeout(preloadSet3Timer);
      clearTimeout(preloadSet4Timer);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSet((prev) => (prev + 1) % IMAGE_SETS.length);
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const currentImages = IMAGE_SETS[currentSet];
  
  // Determine which alt text to use based on current image set
  // Set 0: Default (dark), Set 1: Poke Green, Set 2: Anne&Max, Set 3: Poke
  const getAltText = (device: 'tablet' | 'qr' | 'kiosk' | 'webshop') => {
    if (currentSet === 1 && translations.images?.products) {
      // Poke Green set
      const pokeGreenKey = `${device}PokeGreen` as keyof typeof translations.images.products;
      return translations.images.products[pokeGreenKey] || translations.devices[device].label;
    }
    if (currentSet === 2 && translations.images?.products) {
      // Anne&Max set
      const anneMaxKey = `${device}AnneMax` as keyof typeof translations.images.products;
      return translations.images.products[anneMaxKey] || translations.devices[device].label;
    }
    // Default or Poke set
    return translations.images?.products?.[device] || translations.devices[device].label;
  };

  return (
    <section className={styles.showcase}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {translations.title}
          </h2>
          <RichText className={styles.subtitle} text={translations.subtitle} />
        </div>

        {/* Devices Grid */}
        <div className={styles.devicesGrid}>
          {/* Tablet */}
          <Link href={getProductUrl('tablet')} className={styles.deviceCard}>
            <div className={styles.mockupImage}>
              <Image
                src={currentImages.tablet}
                alt={getAltText('tablet')}
                width={1200}
                height={900}
                className={styles.image}
                quality={75}
                priority
                sizes="(max-width: 768px) 251px, (max-width: 1200px) 200px, (max-width: 1400px) 240px, 280px"
              />
            </div>
            <div className={styles.deviceBadge}>
              <h3 className={styles.deviceLabel}>{translations.devices.tablet.label}</h3>
              {translations.devices.tablet.description && (
                <p className={styles.deviceDescription}>{translations.devices.tablet.description}</p>
              )}
            </div>
          </Link>

          {/* QR */}
          <Link href={getProductUrl('qr')} className={styles.deviceCard}>
            <div className={styles.mockupImage}>
              <Image
                src={currentImages.qr}
                alt={getAltText('qr')}
                width={1200}
                height={900}
                className={styles.image}
                quality={75}
                priority
                sizes="(max-width: 768px) 251px, (max-width: 1200px) 160px, (max-width: 1400px) 190px, 220px"
              />
            </div>
            <div className={styles.deviceBadge}>
              <h3 className={styles.deviceLabel}>{translations.devices.qr.label}</h3>
              {translations.devices.qr.description && (
                <p className={styles.deviceDescription}>{translations.devices.qr.description}</p>
              )}
            </div>
          </Link>

          {/* Kiosk */}
          <Link href={getProductUrl('kiosk')} className={styles.deviceCard}>
            <div className={styles.mockupImage}>
              <Image
                src={currentImages.kiosk}
                alt={getAltText('kiosk')}
                width={1200}
                height={900}
                className={styles.image}
                quality={75}
                loading="lazy"
                sizes="(max-width: 768px) 220px, (max-width: 1200px) 240px, (max-width: 1400px) 280px, 320px"
              />
            </div>
            <div className={styles.deviceBadge}>
              <h3 className={styles.deviceLabel}>{translations.devices.kiosk.label}</h3>
              {translations.devices.kiosk.description && (
                <p className={styles.deviceDescription}>{translations.devices.kiosk.description}</p>
              )}
            </div>
          </Link>

          {/* Webshop */}
          <Link href={getProductUrl('webshop')} className={styles.deviceCard}>
            <div className={styles.mockupImage}>
              <Image
                src={currentImages.webshop}
                alt={getAltText('webshop')}
                width={1200}
                height={900}
                className={styles.image}
                quality={75}
                loading="lazy"
                sizes="(max-width: 768px) 220px, (max-width: 1200px) 320px, (max-width: 1400px) 380px, 450px"
              />
            </div>
            <div className={styles.deviceBadge}>
              <h3 className={styles.deviceLabel}>{translations.devices.webshop.label}</h3>
              {translations.devices.webshop.description && (
                <p className={styles.deviceDescription}>{translations.devices.webshop.description}</p>
              )}
            </div>
          </Link>
        </div>
        
        {/* CTA Button */}
        {ctaText && ctaUrl && (
          <div className={styles.ctaWrapper}>
            <Link href={ctaUrl}>
              <Button
                variant="primary"
                size="lg"
                data-cta-id={ctaId}
                data-cta-position={ctaPosition}
                data-cta-variant="primary"
              >
                {ctaText}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
